import { MIGRATION_KEYS, SESSION_TYPES, STORAGE_KEYS } from '@/utils/infrastructure/auth/sessionConstants';
import {
  clearSession,
  copySession,
  findAutomyUserId,
  getCurrentSession,
  getItem,
  getSessionToken,
  hasUsableAutomyToken,
  setCurrentSession,
  setItem,
} from '@/utils/infrastructure/auth/sessionManager';
import { fetchAutomyRefresh, fetchAutomySignIn } from './portalAuthFetch';

const readStoredToken = (raw) => {
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return data?.accessToken ? data : null;
  } catch (e) {
    return null;
  }
};

export const automyToken = (userId) => {
  if (!userId) return null;
  return readStoredToken(getItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_TOKEN));
};

export const persistAutomyToken = (userId, token) => {
  if (!userId || !token?.accessToken) return token;
  const next = { ...token, issuedAt: Date.now() };
  setItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_TOKEN, JSON.stringify(next));
  return next;
};

const PORTAL_SAFE_COPY = MIGRATION_KEYS.filter(
  (key) =>
    key !== STORAGE_KEYS.AUTH_TOKEN &&
    key !== STORAGE_KEYS.AUTHORITY &&
    key !== STORAGE_KEYS.USER_CONTEXT &&
    key !== STORAGE_KEYS.PORTAL_CONFIG,
);

const dropPortalIndex = (session, userId) => {
  if (session?.type === SESSION_TYPES.PORTAL && session.userId) {
    clearSession(SESSION_TYPES.PORTAL, session.userId, session.resourceId);
  }
  if (userId) {
    setCurrentSession({ type: SESSION_TYPES.AUTOMY, userId, resourceId: null });
  }
};

/**
 * Automy JWT to mint a portal. Never flips the session index — a live PORTAL
 * tab must keep its token until the new portal JWT is written.
 */
export async function resolveAutomyAccessToken(userId) {
  if (hasUsableAutomyToken(userId)) return automyToken(userId);

  const session = getCurrentSession();
  const portalTok = session?.type === SESSION_TYPES.PORTAL ? getSessionToken() : null;
  const stored = automyToken(userId);
  const source = portalTok?.accessToken ? portalTok : stored;
  if (!source?.accessToken) return null;

  try {
    const minted = await fetchAutomySignIn(source.accessToken, {});
    if (minted?.accessToken) return persistAutomyToken(userId, minted);
  } catch (firstError) {
    if (firstError?.status === 401 && stored?.refreshToken) {
      const refreshed = persistAutomyToken(userId, await fetchAutomyRefresh(stored));
      if (!refreshed?.accessToken) return null;
      const minted = await fetchAutomySignIn(refreshed.accessToken, {});
      if (minted?.accessToken) return persistAutomyToken(userId, minted);
    }
  }
  return null;
}

export function activatePortalSession(userId, portalId, minted) {
  if (!userId || !portalId || !minted?.accessToken) return false;
  const portalSession = {
    type: SESSION_TYPES.PORTAL,
    userId,
    resourceId: String(portalId),
  };
  const current = getCurrentSession();
  if (
    current?.userId &&
    (current.type !== SESSION_TYPES.PORTAL || String(current.resourceId) !== String(portalId))
  ) {
    copySession(current, portalSession, PORTAL_SAFE_COPY);
  }
  setCurrentSession(portalSession);
  setItem(
    SESSION_TYPES.PORTAL,
    userId,
    String(portalId),
    STORAGE_KEYS.AUTH_TOKEN,
    JSON.stringify({ ...minted, issuedAt: Date.now() }),
  );
  return true;
}

export async function mintPortalSession(userId, portalId) {
  if (!userId || !portalId) return { ok: false };
  const automy = await resolveAutomyAccessToken(userId);
  if (!automy?.accessToken) return { ok: false };

  try {
    const minted = await fetchAutomySignIn(automy.accessToken, { portalId: String(portalId) });
    if (minted?.twoFactorRequired) return { ok: false, twoFactor: minted, password: automy };
    if (!minted?.accessToken) return { ok: false };
    activatePortalSession(userId, portalId, minted);
    return { ok: true, minted };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export const isCurrentPortal = (portalId) => {
  if (!portalId) return false;
  const session = getCurrentSession();
  if (session?.type !== SESSION_TYPES.PORTAL || !session.userId) return false;
  if (String(session.resourceId) !== String(portalId)) return false;
  return !!getSessionToken()?.accessToken;
};

export async function switchToAutomySession() {
  const session = getCurrentSession();
  const userId = session?.userId || findAutomyUserId();

  if (userId && hasUsableAutomyToken(userId)) {
    dropPortalIndex(session, userId);
    return { ok: true, minted: null };
  }

  const automy = await resolveAutomyAccessToken(userId);
  if (automy?.accessToken) {
    persistAutomyToken(userId, automy);
    dropPortalIndex(session, userId);
    return { ok: true, minted: automy };
  }

  return { ok: false };
}
